import React from "react";

import { Button, LeadSenderAvatar, Sidebar } from "../../ui";
import {
  DEFAULT_LINKEDIN_ACCOUNT_AVATAR_SRC,
  linkedinAccountAvatarSrc,
} from "../../../utils/linkedinProfilePicture";
import SenderLimitControl from "./SenderLimitControl";

export const DEFAULT_SENDER_DAILY_QUOTA_MAX = 50;

export type SenderLimits = {
  maxFollowsPerDay: number;
  maxMessagesPerDay: number;
  maxConnectionRequestsPerDay: number;
  maxProfileViewsPerDay: number;
};

type SenderLimitsSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  senderName: string;
  senderAvatarSrc?: string;
  /**
   * "legacy" — same avatar behavior as before (img + chirag SVG fallback). Used by campaigns.
   * "sender" — LeadSenderAvatar (RxAvatar when no photo), aligned with Leads/Accounts.
   */
  linkedInAvatarVariant?: "legacy" | "sender";
  value: SenderLimits;
  max?: number;
  onChange: (next: SenderLimits) => void;
  onSave?: () => void;
  readOnly?: boolean;
};

export default function SenderLimitsSidebar({
  isOpen,
  onClose,
  senderName,
  senderAvatarSrc = "",
  linkedInAvatarVariant = "legacy",
  value,
  max = DEFAULT_SENDER_DAILY_QUOTA_MAX,
  onChange,
  onSave,
  readOnly = false,
}: SenderLimitsSidebarProps) {
  const setField = <K extends keyof SenderLimits>(key: K, nextValue: number) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Sender Limits" size="medium">
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          {linkedInAvatarVariant === "sender" ? (
            <LeadSenderAvatar picture={senderAvatarSrc} className="h-10 w-10" />
          ) : (
            <img
              src={linkedinAccountAvatarSrc(senderAvatarSrc)}
              alt=""
              aria-hidden
              className="h-10 w-10 rounded-full object-cover bg-[#eef1f4]"
              onError={(e) => {
                const el = e.currentTarget;
                if (el.dataset.avatarFallback === "1") return;
                el.dataset.avatarFallback = "1";
                el.src = DEFAULT_LINKEDIN_ACCOUNT_AVATAR_SRC;
              }}
            />
          )}
          <p className="text-md font-medium text-dark-black">{senderName}</p>
        </div>

        <div className="rounded-[10px] border border-brand-blue bg-bg-light-blue px-3 py-3">
          <p className="text-sm leading-normal font-medium">
            <span className="text-dark-black">Note:</span>{" "}
            <span className="text-brand-blue">
              The numbers below may vary based on your account&apos;s health and activities on other campaigns. We do
              this to keep your accounts safe.
            </span>
          </p>
        </div>

      <div className="pt-4 space-y-6">
        <SenderLimitControl
          label="Max follows/day"
          value={value.maxFollowsPerDay}
          max={max}
          readOnly={readOnly}
          onChange={(next) => setField("maxFollowsPerDay", next)}
        />
        <SenderLimitControl
          label="Max Messages/Day"
          value={value.maxMessagesPerDay}
          max={max}
          readOnly={readOnly}
          onChange={(next) => setField("maxMessagesPerDay", next)}
        />
        <SenderLimitControl
          label="Max Connection Requests/Day"
          value={value.maxConnectionRequestsPerDay}
          max={max}
          readOnly={readOnly}
          onChange={(next) => setField("maxConnectionRequestsPerDay", next)}
        />
        <SenderLimitControl
          label="Max Profile Views/Day"
          value={value.maxProfileViewsPerDay}
          max={max}
          readOnly={readOnly}
          onChange={(next) => setField("maxProfileViewsPerDay", next)}
        />
        </div>

        <div className="flex justify-end border-t border-light-grey pt-12">
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            isDisabled={readOnly || !onSave}
            iconSrc="/icons/correct.svg"
            showBefore
          >
            Save Settings
          </Button>
        </div>
      </div>
    </Sidebar>
  );
}
